import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import AnalyticsApiService, { AppUser, AppUserUpdate } from '../services/analyticsApi';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

interface UserProfilesFeedPageProps {}

const USERS_PER_PAGE = 100;

export function UserProfilesFeedPage({}: UserProfilesFeedPageProps) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<AppUser | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Form state
  const [form, setForm] = useState({
    username: '',
    phone: '',
    fname: '',
    lname: '',
    location: '',
    onboarded: false,
  });

  // Check if user has sudo/admin permissions
  const isSudo = currentUser?.role?.permissions?.manage_users || currentUser?.is_admin || false;

  // Sort and filter users alphabetically by first name (handle null values)
  const sortedUsers = useMemo(() => {
    let filtered = users;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = users.filter((user) => {
        const username = (user.username || '').toLowerCase();
        const phone = (user.phone || '').toLowerCase();
        const fname = (user.fname || '').toLowerCase();
        const lname = (user.lname || '').toLowerCase();
        
        return (
          username.includes(query) ||
          phone.includes(query) ||
          fname.includes(query) ||
          lname.includes(query)
        );
      });
    }
    
    // Sort alphabetically by first name
    return filtered.sort((a, b) => (a.fname || '').localeCompare(b.fname || ''));
  }, [users, searchQuery]);

  const loadUsers = async (resetList: boolean = false) => {
    if (resetList) {
      setLoading(true);
      setOffset(0);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);
    
    try {
      const currentOffset = resetList ? 0 : offset;
      const data = await AnalyticsApiService.getAppUsers(USERS_PER_PAGE, currentOffset, false);
      
      if (resetList) {
        setUsers(data);
        setOffset(USERS_PER_PAGE);
      } else {
        setUsers(prev => [...prev, ...data]);
        setOffset(prev => prev + USERS_PER_PAGE);
      }
      
      // If we got fewer users than requested, we've reached the end
      if (data.length < USERS_PER_PAGE) {
        setHasMore(false);
      }
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Failed to load users.');
      toast.error(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreUsers = useCallback(async () => {
    if (!hasMore || loadingMore || loading) return;
    await loadUsers(false);
  }, [hasMore, loadingMore, loading, offset]);

  useEffect(() => {
    loadUsers(true);
  }, []);

  // Infinite scroll hook
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: loadMoreUsers,
    hasMore,
    isLoading: loadingMore || loading,
    enabled: true,
  });

  const openEditor = (user: AppUser) => {
    setEditingUser(user);
    setForm({
      username: user.username || '',
      phone: user.phone || '',
      fname: user.fname || '',
      lname: user.lname || '',
      location: user.location || '',
      onboarded: user.onboarded || false,
    });
  };

  const closeEditor = () => {
    setEditingUser(null);
    setForm({
      username: '',
      phone: '',
      fname: '',
      lname: '',
      location: '',
      onboarded: false,
    });
  };

  const saveUser = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const updates: AppUserUpdate = {
        username: form.username,
        phone: form.phone,
        fname: form.fname,
        lname: form.lname,
        location: form.location || null,
        onboarded: form.onboarded,
      };
      await AnalyticsApiService.updateAppUser(editingUser.id, updates);
      toast.success('User updated successfully');
      await loadUsers(true);
      closeEditor();
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Failed to update user.');
      toast.error(err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (user: AppUser) => {
    setDeletingUser(user);
  };

  const deleteUser = async () => {
    if (!deletingUser) return;
    setDeleteLoading(true);
    try {
      await AnalyticsApiService.deleteAppUser(deletingUser.id);
      toast.success('User deleted successfully');
      await loadUsers(true);
      setDeletingUser(null);
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Failed to delete user.');
      toast.error(err.message || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="w-full h-full overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            User Profiles Feed
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Inspect and manage Series app users. Editing and deletion requires sudo access.
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-100">
            {error}
          </div>
        )}

        {/* Users Table Card */}
        <div className="backdrop-blur-xl bg-white/70 dark:bg-gray-800/70 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl overflow-hidden">
          {/* Card Header */}
          <div className="border-b border-gray-200/50 dark:border-gray-700/50 px-6 py-4 bg-white/50 dark:bg-gray-800/50 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Total users</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {sortedUsers.length} {searchQuery ? 'filtered' : 'records'} {users.length !== sortedUsers.length ? `of ${users.length}` : ''}
                </p>
              </div>
              <button
                onClick={() => loadUsers(true)}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            
            {/* Search Input */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search by username, phone, or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50/50 dark:bg-gray-900/30 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Contact</th>
                  <th className="px-6 py-3">Location</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">AI Friend</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200/50 dark:divide-gray-700/50">
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      Loading users...
                    </td>
                  </tr>
                )}
                {!loading && sortedUsers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      No users found.
                    </td>
                  </tr>
                )}
                {!loading &&
                  sortedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {user.fname || '—'} {user.lname || '—'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{user.username || '—'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-900 dark:text-white">{user.phone || '—'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-500 dark:text-gray-400">{user.location || '—'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            user.onboarded
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                          }`}
                        >
                          {user.onboarded ? 'Onboarded' : 'Invited'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-500 dark:text-gray-400">{user.ai_friend_id || '—'}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEditor(user)}
                            disabled={!isSudo}
                            className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Edit
                          </button>
                          {isSudo && (
                            <button
                              onClick={() => confirmDelete(user)}
                              className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          
          {/* Infinite Scroll Sentinel */}
          {!loading && hasMore && (
            <div ref={sentinelRef} className="py-8 text-center">
              {loadingMore && (
                <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-sm">Loading more users...</span>
                </div>
              )}
            </div>
          )}
          
          {!hasMore && users.length > 0 && (
            <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
              All users loaded ({users.length} total)
            </div>
          )}
        </div>

        {/* Edit Modal */}
        {editingUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit User</h2>
                <button
                  onClick={closeEditor}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">First name</label>
                  <input
                    type="text"
                    value={form.fname}
                    onChange={(e) => setForm({ ...form, fname: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Last name</label>
                  <input
                    type="text"
                    value={form.lname}
                    onChange={(e) => setForm({ ...form, lname: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Username</label>
                  <input
                    type="text"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Location</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Onboarded</label>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, onboarded: !form.onboarded })}
                    className={`w-full h-10 rounded-lg border px-3 text-left text-sm transition-colors ${
                      form.onboarded
                        ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {form.onboarded ? 'Yes' : 'No'}
                  </button>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={closeEditor}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveUser}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6"
            >
              <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-3">Delete User</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to delete {deletingUser.fname || 'this user'} {deletingUser.lname || ''}? This action can be
                reversed by reactivating the user later.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeletingUser(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteUser}
                  disabled={deleteLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}

